import React, { useState, useEffect } from "react";
import { IconInfoCircle } from "@tabler/icons-react";
import { Stack } from "@altimateai/lego";
import { Button, Alert } from "@altimateai/lego";

// Version check interval in milliseconds (e.g., every 5 minutes)
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000;

// Store the current version hash in memory
let currentVersionHash: string | null = null;

// Function to get the current version hash from CloudFront
const fetchVersionHash = async (): Promise<string> => {
  try {
    // Add cache-busting query parameter to prevent CloudFront from serving cached version
    const response = await fetch(`/version.json?_=${Date.now()}`);
    if (!response.ok) {
      throw new Error("Failed to fetch version");
    }
    // Local dev servers fall back to ``index.html`` for unknown paths, which
    // returns ``200 OK`` with an HTML body and trips ``response.json()``.
    // Bail out unless the server actually replied with JSON.
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.toLowerCase().includes("application/json")) {
      return "fallback-version-hash";
    }
    const data = await response.json();
    return data.version;
  } catch (error) {
    console.error("Error fetching version:", error);
    // Return current timestamp as fallback
    return "fallback-version-hash";
  }
};

// Function to check if there's a new version
const checkVersion = async (onVersionUpdate: () => void): Promise<void> => {
  try {
    const newVersionHash = await fetchVersionHash();

    if (newVersionHash === "fallback-version-hash") {
      // If we couldn't fetch a valid version, skip the check
      return;
    }

    // If this is the first check, just store the version
    if (!currentVersionHash) {
      currentVersionHash = newVersionHash;
      return;
    }

    // If version has changed, call the callback
    if (newVersionHash !== currentVersionHash) {
      onVersionUpdate();
    }
  } catch (error) {
    console.error("Error checking version:", error);
  }
};

// Main version checker component
export const VersionChecker: React.FC = () => {
  const [showVersionAlert, setShowVersionAlert] = useState(false);

  useEffect(() => {
    // Start version check when component mounts
    const timer = setInterval(() => {
      checkVersion(() => setShowVersionAlert(true));
    }, VERSION_CHECK_INTERVAL);

    // Check immediately on start
    checkVersion(() => setShowVersionAlert(true));

    return () => clearInterval(timer);
  }, []);

  const handleReload = () => {
    window.location.reload();
  };

  if (!showVersionAlert) {
    return null;
  }

  return (
    <Alert
      variant="default"
      className="al-border-orange-500 al-bg-orange-50 al-border-0 al-border-b al-mb-0 al-rounded-none !al-p-2 al-z-[51]"
    >
      <Stack justify="between" align="center" className="al-flex-1">
        <div>
          <Stack align="center">
            <IconInfoCircle className="al-h-6 al-w-6 al-text-orange-500" />
            We&apos;ve improved your experience with new features. Reload now to access the improved
            experience.
            <Button
              onClick={handleReload}
              size="xs"
              variant="outline"
              className="al-border-orange-500 al-text-orange-500 hover:al-text-orange-700"
            >
              Reload
            </Button>
          </Stack>
        </div>
      </Stack>
    </Alert>
  );
};
